import { atom, useRecoilState } from 'recoil';
/* eslint-disable-next-line import/no-cycle */
import {
  FormField,
  useVendorProgramTemplate,
  VendorProgramTemplateKeys,
} from './program-template';
import { useTranslate } from '@im/react-localization';
import _ from 'lodash';

export type FieldError = {
  groupId: FormField['groupId'];
  message: string;
};

export type TemplateError = Record<VendorProgramTemplateKeys, FieldError>;

const VendorProgramTemplateErrorAtom = atom<TemplateError>({
  default: {} as TemplateError,
  key: 'vendor-program-template-error',
});

export function useVendorProgramTemplateError() {
  const [templateError, _setTemplateError] = useRecoilState(
    VendorProgramTemplateErrorAtom
  );

  const setTemplateError = (
    templateError: Parameters<typeof _setTemplateError>[0]
  ) => {
    _setTemplateError(templateError);
  };
  return [templateError, setTemplateError] as const;
}

export const programNameRegex = /(^[a-zA-Z\d]+[a-zA-Z\d_]*[a-zA-Z\d])$/;
export const vendorCodeRegex = /^(?:[a-zA-Z0-9]+(?:,[a-zA-Z0-9]+)*)?$/;
export const ALPHA_ONLY_REGEX = /^[A-Za-z]*$/;

export const useValidateTemplateField = () => {
  const translate = useTranslate();

  return (
    templateInfo: ReturnType<typeof useVendorProgramTemplate>[0],
    fieldKey: VendorProgramTemplateKeys,
    vendorProgramTemplateFields: Record<VendorProgramTemplateKeys, FormField>
  ) => {
    const fieldErrors = {} as Record<VendorProgramTemplateKeys, FieldError>;

    const resetError = (fieldKey: VendorProgramTemplateKeys) => {
      if (!fieldErrors[fieldKey]?.message)
        fieldErrors[fieldKey] = {
          groupId: vendorProgramTemplateFields[fieldKey]?.groupId,
          message: '',
        };
    };

    Object.keys(templateInfo).forEach((key) => {
      if (fieldKey === key && vendorProgramTemplateFields[fieldKey]?.required) {
        if (
          templateInfo[fieldKey] === undefined ||
          templateInfo[fieldKey] === null ||
          templateInfo[fieldKey] === ''
        ) {
          fieldErrors[fieldKey] = {
            groupId: vendorProgramTemplateFields[fieldKey]?.groupId,
            message: translate(
              vendorProgramTemplateFields[fieldKey]?.options?.length
                ? 'Common.PleaseSelectValue'
                : 'Common.PleaseEnterValue',
              {
                value: translate(
                  `VendorProgram.ProgramDetail${_.upperFirst(fieldKey)}Name`
                ),
              }
            ),
          };
        } else {
          fieldErrors[fieldKey] = {
            groupId: vendorProgramTemplateFields[fieldKey]?.groupId,
            message: '',
          };
        }
      }
    });

    switch (fieldKey) {
      case 'vendorprogramsname':
        if (!programNameRegex.exec(templateInfo[fieldKey] as string)) {
          fieldErrors[fieldKey] = {
            groupId: vendorProgramTemplateFields[fieldKey]?.groupId,
            message: translate('VendorProgram.AlertProgramDetailFormError'),
          };
        } else {
          resetError(fieldKey);
        }
        break;

      case 'endUserAddressMatchPercentage':
        if (!(templateInfo[fieldKey] as string)?.match(/^\d\d?$|^100$/gm)) {
          fieldErrors[fieldKey] = {
            groupId: vendorProgramTemplateFields[fieldKey]?.groupId,
            message: translate('VendorProgram.AlertEndUserPercentageError'),
          };
        } else {
          resetError(fieldKey);
        }
        break;

      case 'vendorcode':
        if (!vendorCodeRegex.exec(templateInfo[fieldKey] as string)) {
          fieldErrors[fieldKey] = {
            groupId: vendorProgramTemplateFields[fieldKey]?.groupId,
            message: translate('VendorProgram.AlertVenorCodeError'),
          };
        } else {
          resetError(fieldKey);
        }
        break;

      case 'acopGracePeriod':
        if (
          templateInfo[fieldKey] !== '' &&
          templateInfo[fieldKey] !== null &&
          templateInfo[fieldKey] !== undefined &&
          (!/^\d+$/.test(String(templateInfo[fieldKey])) ||
            Number(templateInfo[fieldKey]) < 0)
        ) {
          fieldErrors[fieldKey] = {
            groupId: vendorProgramTemplateFields[fieldKey]?.groupId,
            message: translate('VendorProgram.AlertPositiveIntegerError'),
          };
        } else {
          resetError(fieldKey);
        }
        break;

      case 'impulseDebitVRFPPercentage':
      case 'impulseDebitVRFVPercentage':
      case 'impulseDebitEnOPercentage':
      case 'impulseDebitMDFPercentage':
      case 'impulseDebitOTHPercentage':
      case 'impulseDebitVendorPercentage': {
        const value = templateInfo[fieldKey];

        if (typeof value === 'string' && !/^\d*\.?\d*$/.test(value)) {
          fieldErrors[fieldKey] = {
            groupId: vendorProgramTemplateFields[fieldKey]?.groupId,
            message: translate('Common.ValidationNumberMessage'),
          };
          break;
        }

        if (
          Number(templateInfo['impulseDebitVRFPPercentage'] ?? 0) +
            Number(templateInfo['impulseDebitVRFVPercentage'] ?? 0) +
            Number(templateInfo['impulseDebitEnOPercentage'] ?? 0) +
            Number(templateInfo['impulseDebitMDFPercentage'] ?? 0) +
            Number(templateInfo['impulseDebitOTHPercentage'] ?? 0) +
            Number(templateInfo['impulseDebitVendorPercentage'] ?? 0) !==
          100
        ) {
          fieldErrors[fieldKey] = {
            groupId: vendorProgramTemplateFields[fieldKey]?.groupId,
            message: translate('VendorProgram.ImpulsePercentageError'),
          };
        } else {
          [
            'impulseDebitVRFPPercentage',
            'impulseDebitVRFVPercentage',
            'impulseDebitEnOPercentage',
            'impulseDebitMDFPercentage',
            'impulseDebitOTHPercentage',
            'impulseDebitVendorPercentage',
          ].forEach((key) => {
            resetError(key as VendorProgramTemplateKeys);
          });
        }
        break;
      }

      default:
        break;
    }

    return fieldErrors;
  };
};

export const useValidateTemplate = () => {
  const validateField = useValidateTemplateField();

  return (
    templateInfo: Partial<ReturnType<typeof useVendorProgramTemplate>[0]>,
    templateFields: Record<VendorProgramTemplateKeys, FormField>
  ) => {
    const templateError: { fieldErrors: TemplateError; hasError: boolean } = {
      fieldErrors: {} as TemplateError,
      hasError: false,
    };

    for (const fieldKey in templateInfo) {
      const fieldValidation = validateField(
        templateInfo,
        fieldKey as VendorProgramTemplateKeys,
        templateFields
      )[fieldKey as VendorProgramTemplateKeys];

      if (fieldValidation?.message) {
        templateError.hasError = true;
      }
      templateError.fieldErrors[fieldKey as VendorProgramTemplateKeys] =
        fieldValidation;
    }

    return templateError;
  };
};
